import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SfDataService } from '../../services/sf-data.service';

interface HelpfulContact {
  id?: number;
  category: string;
  contactName: string;
  email: string;
}

@Component({
  selector: 'app-helpful-contacts-maintenance',
  templateUrl: './helpful-contacts-maintenance.component.html',
  styleUrls: ['./helpful-contacts-maintenance.component.scss'],
  standalone: false
})
export class HelpfulContactsMaintenanceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  contacts: HelpfulContact[] = [];
  isLoading = false;
  isFormVisible = false;
  isEditMode = false;
  editingId: number | null = null;

  form = {
    category: '',
    contactName: '',
    email: ''
  };

  errorMessage = '';
  successMessage = '';

  constructor(private sfDataService: SfDataService) { }

  ngOnInit(): void {
    this.loadContacts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadContacts(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.sfDataService.getHelpfulContacts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.contacts = data;
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = 'Failed to load contacts';
          this.isLoading = false;
          console.error(err);
        }
      });
  }

  openForm(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.resetForm();
    this.isFormVisible = true;
    this.errorMessage = '';
  }

  editContact(contact: HelpfulContact): void {
    this.isEditMode = true;
    this.editingId = contact.id;
    this.form = { ...contact };
    this.isFormVisible = true;
    this.errorMessage = '';
  }

  closeForm(): void {
    this.isFormVisible = false;
    this.resetForm();
  }

  resetForm(): void {
    this.form = {
      category: '',
      contactName: '',
      email: ''
    };
  }

  saveContact(): void {
    if (!this.validateForm()) return;

    if (this.isEditMode && this.editingId !== null) {
      this.updateContact();
    } else {
      this.createContact();
    }
  }

  private createContact(): void {
    this.isLoading = true;
    this.sfDataService.createHelpfulContact(this.form)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Contact created successfully';
          this.closeForm();
          this.loadContacts();
          this.clearMessages();
        },
        error: (err) => {
          this.errorMessage = 'Failed to create contact';
          this.isLoading = false;
          console.error(err);
        }
      });
  }

  private updateContact(): void {
    if (this.editingId === null) return;
    this.isLoading = true;
    this.sfDataService.updateHelpfulContact(this.editingId, this.form)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Contact updated successfully';
          this.closeForm();
          this.loadContacts();
          this.clearMessages();
        },
        error: (err) => {
          this.errorMessage = 'Failed to update contact';
          this.isLoading = false;
          console.error(err);
        }
      });
  }

  deleteContact(id: number): void {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    this.isLoading = true;
    this.sfDataService.deleteHelpfulContact(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Contact deleted successfully';
          this.loadContacts();
          this.clearMessages();
        },
        error: (err) => {
          this.errorMessage = 'Failed to delete contact';
          this.isLoading = false;
          console.error(err);
        }
      });
  }

  private validateForm(): boolean {
    if (!this.form.category?.trim()) {
      this.errorMessage = 'Category is required';
      return false;
    }
    if (!this.form.contactName?.trim()) {
      this.errorMessage = 'Contact name is required';
      return false;
    }
    if (!this.form.email?.trim()) {
      this.errorMessage = 'Email is required';
      return false;
    }
    if (!this.isValidEmail(this.form.email)) {
      this.errorMessage = 'Invalid email format';
      return false;
    }
    return true;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private clearMessages(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }
}
